import * as React from "react";
import { Box, Paper, Stack, ButtonBase, Typography, alpha, useTheme } from "@mui/material";

export type VerticalSubTab = {
	id: string;
	label: React.ReactNode; // JSX do "card" (nome, switch, ícone, etc.)
	disabled?: boolean;
	content: React.ReactNode; // conteúdo à direita
};

type Props = {
	items: VerticalSubTab[];
	value?: string; // controlado (opcional)
	defaultValue?: string; // não-controlado (opcional)
	onChange?: (id: string) => void;
	leftWidth?: number | string;
	rightPadding?: number;
	height?: number | string;
	paper?: boolean;
	itemGap?: number; // espaço entre cards
	itemMinHeight?: number; // altura mínima do card
	dense?: boolean; // padding menor nos cards
	leftHeader?: React.ReactNode;
	leftFooter?: React.ReactNode;
};

export const VerticalSubTabs: React.FC<Props> = ({
	items,
	value,
	defaultValue,
	onChange,
	leftWidth = 260,
	rightPadding = 2,
	height = 520,
	paper = false,
	itemGap = 1,
	itemMinHeight = 52,
	dense = false,
	leftHeader,
	leftFooter,
}) => {
	const uncontrolled = value == null;
	const [internal, setInternal] = React.useState<string>(defaultValue ?? items[0]?.id);
	const active = uncontrolled ? internal : value ?? items[0]?.id;
	const theme = useTheme();
	const listRef = React.useRef<HTMLDivElement>(null);

	const selectByIndex = (idx: number) => {
		const it = items[idx];
		if (!it || it.disabled) return;
		if (uncontrolled) setInternal(it.id);
		onChange?.(it.id);
	};

	const activeIndex = Math.max(
		0,
		items.findIndex((i) => i.id === active)
	);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
		e.preventDefault();

		const enabled = items.map((i) => !i.disabled);
		let next = activeIndex;

		if (e.key === "ArrowDown") {
			for (let i = activeIndex + 1; i < items.length; i++) {
				if (enabled[i]) {
					next = i;
					break;
				}
			}
		} else if (e.key === "ArrowUp") {
			for (let i = activeIndex - 1; i >= 0; i--) {
				if (enabled[i]) {
					next = i;
					break;
				}
			}
		} else if (e.key === "Home") {
			for (let i = 0; i < items.length; i++) {
				if (enabled[i]) {
					next = i;
					break;
				}
			}
		} else if (e.key === "End") {
			for (let i = items.length - 1; i >= 0; i--) {
				if (enabled[i]) {
					next = i;
					break;
				}
			}
		}

		if (next !== activeIndex) selectByIndex(next);
	};

	return (
		<Box sx={{ display: "flex", gap: 2, height, overflow: "hidden" }}>
			{/* COLUNA ESQUERDA (lista como cards) */}
			<Paper
				sx={{
					width: leftWidth,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					boxShadow: "none",
				}}
			>
				{leftHeader && <Box sx={{ p: 1.25, borderBottom: `1px solid ${theme.palette.divider}` }}>{leftHeader}</Box>}

				<Stack
					ref={listRef}
					role="tablist"
					aria-orientation="vertical"
					onKeyDown={handleKeyDown}
					sx={{
						p: 1,
						gap: itemGap,
						overflow: "auto",
						flex: 1,
					}}
				>
					{items.map((it, idx) => {
						const selected = it.id === active;
						return (
							<Paper
								key={it.id}
								elevation={selected ? 3 : 0}
								sx={{
									borderRadius: 2,
									border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
									bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
									opacity: it.disabled ? 0.6 : 1,
								}}
							>
								<ButtonBase
									role="tab"
									aria-selected={selected}
									disabled={!!it.disabled}
									onClick={() => selectByIndex(idx)}
									sx={{
										width: "100%",
										justifyContent: "stretch",
										textAlign: "left",
										borderRadius: 2,
										p: dense ? 1 : 1.5,
										minHeight: itemMinHeight,
										alignItems: "center",
									}}
								>
									{/* O "label" vem de fora; aqui só garantimos layout */}
									<Box sx={{ width: "100%" }}>
										{typeof it.label === "string" ? (
											<Typography variant="body1" noWrap>
												{it.label}
											</Typography>
										) : (
											it.label
										)}
									</Box>
								</ButtonBase>
							</Paper>
						);
					})}
				</Stack>

				{leftFooter && <Box sx={{ p: 1.25, borderTop: `1px solid ${theme.palette.divider}` }}>{leftFooter}</Box>}
			</Paper>

			{/* CONTEÚDO À DIREITA */}
			<Box sx={{ flex: 1, overflow: "auto", pr: rightPadding }}>
				{items.map((it) => (
					<Box key={it.id} role="tabpanel" hidden={active !== it.id} sx={{ height: "100%", display: active === it.id ? "block" : "none" }}>
						<Stack sx={{ minWidth: 0 }}>{it.content}</Stack>
					</Box>
				))}
			</Box>
		</Box>
	);
};
